import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AlertTriangle, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import {
  CourseCount,
  CourseSegmentBar,
  CourseStatusChip,
  CourseStepMarker,
} from '@/components/patient/course/CourseChrome';
import { deriveCourseActionItems } from '@/lib/courseRequiresAction';
import { courseProgressLabel, deriveCourseStepVisual } from '@/lib/courseStepStatus';
import {
  formatCourseDateTimeWithToday,
  formatCourseMiniDay,
  formatCoursePeriod,
} from '@/lib/courseDateFormat';
import {
  courseListActionSummary,
  coursePeriod,
  coursePersonNames,
  coursePractitionerNames,
  courseStepTitle,
  nextCourseStep,
} from '@/lib/courseUiHelpers';
import type { StaffTreatmentCourse } from '@/lib/treatmentCourseStaffView';

export function CourseListActiveCard({
  course,
  onOpen,
  openStepSearch,
}: {
  course: StaffTreatmentCourse;
  onOpen: () => void;
  /** Query string (no leading ?) that opens this step — same params as «Åbn trin». */
  openStepSearch: (stepId: string) => string;
}) {
  const { t, i18n } = useTranslation();
  const progress = courseProgressLabel(course.steps);
  const next = nextCourseStep(course.steps);
  const broken = new Set(
    course.interval_warnings.filter((row) => row.is_broken).map((row) => row.step_id),
  );
  const actions = useMemo(
    () => deriveCourseActionItems(course.steps, broken),
    [broken, course.steps],
  );
  const period = coursePeriod(course.steps);
  const practitioners = coursePractitionerNames(course.steps);
  const nextNames = next ? coursePersonNames(next.practitioners) : '';

  return (
    <article className="cl-card act" data-testid="course-list-card">
      <div>
        <div className="cl-t">
          <h3>
            <button
              type="button"
              className="cl-title"
              onClick={onOpen}
              data-testid="course-list-title"
            >
              {course.template_name || t('treatment_courses.summary_untitled')}
            </button>
          </h3>
          <CourseStatusChip status={course.status} />
        </div>
        <p className="cl-m num">
          {t('treatment_courses.list_from_template', {
            name: course.template_name || t('treatment_courses.summary_untitled'),
            steps: t('treatment_courses.step_count', { count: course.steps.length }),
          })}
          {period.start ? ` · ${formatCoursePeriod(period.start, period.end, i18n.language)}` : ''}
          {practitioners.length ? ` · ${practitioners.join(', ')}` : ''}
        </p>
        <div className="cl-prog">
          <CourseSegmentBar
            total={progress.total}
            completed={progress.completed}
            currentIndex={Math.max(progress.n - 1, 0)}
          />
          <b className="num">
            {t('treatment_courses.completed_of_total', {
              completed: progress.completed,
              total: progress.total,
            })}
          </b>
        </div>
        <nav className="mini-steps num" aria-label={t('treatment_courses.section_steps')}>
          {course.steps.map((step) => {
            const title = courseStepTitle(step, t('treatment_courses.step_no_service'));
            const when = step.starts_at
              ? formatCourseMiniDay(
                  step.starts_at,
                  i18n.language,
                  t('treatment_courses.today_chip'),
                )
              : '';
            const visible = when ? `${title} · ${when}` : title;
            const current = next?.id === step.id;
            const search = openStepSearch(step.id);
            return (
              <Link
                key={step.id}
                to={{ search: search.startsWith('?') ? search : `?${search}` }}
                className={current ? 'cur' : undefined}
                aria-current={current ? 'step' : undefined}
                aria-label={`${t('treatment_courses.open_step_n', { n: step.sort_order })}: ${visible}`}
                data-testid="course-list-step"
                data-step-id={step.id}
              >
                <CourseStepMarker
                  visual={deriveCourseStepVisual(course.steps, step.id)}
                  size="sm"
                />
                {visible}
              </Link>
            );
          })}
        </nav>
      </div>
      <div className="cl-side">
        <div className="cl-next">
          <div className="k">
            {t('treatment_courses.list_next_appointment', {
              step: next
                ? t('treatment_courses.step_of_total', {
                    n: next.sort_order,
                    total: course.steps.length,
                  })
                : t('treatment_courses.none'),
            })}
          </div>
          <div className="v num">
            {next?.starts_at
              ? formatCourseDateTimeWithToday(
                  next.starts_at,
                  i18n.language,
                  t('treatment_courses.today_chip'),
                )
              : t('treatment_courses.book_not_booked')}
          </div>
          {next ? (
            <div className="s">
              {courseStepTitle(next, t('treatment_courses.step_no_service'))}
              {nextNames ? ` · ${nextNames}` : ''}
            </div>
          ) : null}
        </div>
        {actions.length > 0 ? (
          <div className="cl-warn">
            <AlertTriangle className="i sm" aria-hidden />
            <div>
              <b>{t('treatment_courses.list_action_count', { count: actions.length })}</b>
              <br />
              {courseListActionSummary(actions, t)}
            </div>
          </div>
        ) : null}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button type="button" variant="secondary" onClick={onOpen}>
            {t('treatment_courses.list_open_course')}
          </Button>
        </div>
      </div>
    </article>
  );
}

export function CourseListClosedRow({
  course,
  onOpen,
}: {
  course: StaffTreatmentCourse;
  onOpen: () => void;
}) {
  const { t, i18n } = useTranslation();
  const progress = courseProgressLabel(course.steps);
  const period = coursePeriod(course.steps);
  const practitioners = coursePractitionerNames(course.steps);

  return (
    <button type="button" onClick={onOpen} className="cl-row" data-testid="course-list-closed-row">
      <div className="grow">
        <div className="t">{course.template_name || t('treatment_courses.summary_untitled')}</div>
        <div className="m num">
          {t('treatment_courses.list_from_template', {
            name: course.template_name || t('treatment_courses.summary_untitled'),
            steps: t('treatment_courses.step_count', { count: course.steps.length }),
          })}
          {period.start ? ` · ${formatCoursePeriod(period.start, period.end, i18n.language)}` : ''}
          {practitioners.length ? ` · ${practitioners.join(', ')}` : ''}
        </div>
      </div>
      <div className="pg num">
        <CourseSegmentBar total={progress.total} completed={progress.completed} faded />
        {t('treatment_courses.completed_of_total_short', {
          completed: progress.completed,
          total: progress.total,
        })}
      </div>
      <CourseStatusChip status={course.status} />
      <span className="chev">
        <ChevronRight className="i sm" aria-hidden />
      </span>
    </button>
  );
}

export function CourseListGroup({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: ReactNode;
}) {
  return (
    <section>
      <div className="grp-h2">
        {title} <CourseCount value={count} />
      </div>
      {children}
    </section>
  );
}
